'use client';

import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from '@/lib/icons';

interface CarouselSlideProps {
  children: React.ReactNode;
}

export const CarouselSlide: React.FC<CarouselSlideProps> = ({ children }) => {
  return <div className="w-full flex-shrink-0" style={{ textWrap: 'pretty' }}>{children}</div>;
};

interface CarouselProps {
  children: React.ReactNode;
  autoPlay?: boolean;
  interval?: number;
  showArrows?: boolean;
  showDots?: boolean;
  className?: string;
  schemaType?: 'Review' | 'none';
}

export const Carousel: React.FC<CarouselProps> = ({ 
  children, 
  autoPlay = false,
  interval = 5000,
  showArrows = true,
  showDots = true,
  className = '',
  schemaType = 'none'
}) => {
  const slides = React.Children.toArray(children);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isEnhanced, setIsEnhanced] = useState(false);

  // Mark as enhanced when JS loads
  React.useEffect(() => {
    setIsEnhanced(true);
    
    // Inject CSS for no-jump behavior
    if (!document.getElementById('carousel-base-styles')) {
      const style = document.createElement('style');
      style.id = 'carousel-base-styles';
      style.textContent = `
        /* All slides visible for SEO but positioned for carousel */
        .carousel-slides {
          display: flex;
          transition: transform 0.3s ease-in-out;
        }
        
        /* Semantic structure for screen readers */
        .carousel-container[aria-roledescription="carousel"] {
          position: relative;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Auto-play functionality
  React.useEffect(() => {
    if (autoPlay && !isHovered && slides.length > 1 && isEnhanced) {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, interval);
      
      return () => clearInterval(timer);
    }
  }, [autoPlay, interval, isHovered, slides.length, isEnhanced]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  // Touch handling for mobile
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd || !isEnhanced) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextSlide();
    }
    if (isRightSwipe) {
      prevSlide();
    }
  };

  // Generate Review schema if needed
  const reviewSchema = schemaType === 'Review' && (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "itemListElement": slides.map((_, index) => ({
            "@type": "Review",
            "position": index + 1,
            "reviewRating": {
              "@type": "Rating",
              "ratingValue": 5,
              "bestRating": 5
            }
          }))
        })
      }}
    />
  );

  return (
    <>
      {reviewSchema}
      <div 
        className={`carousel-container relative overflow-hidden my-8 mdx-component-base ${className}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        role="region"
        aria-roledescription="carousel"
        aria-label="Content carousel"
        data-enhanced={isEnhanced}
      >
        {/* Live region for accessibility */}
        <div className="sr-only" aria-live="polite" aria-atomic="true">
          Slide {currentSlide + 1} of {slides.length}
        </div>

        {/* Slides Container */}
        <div 
          className="carousel-slides flex transition-transform duration-300 ease-in-out"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
          aria-label={`Slide ${currentSlide + 1}`}
        >
          {slides.map((slide, index) => (
            <div
              key={index}
              role="group"
              aria-roledescription="slide"
              aria-label={`${index + 1} of ${slides.length}`}
              aria-hidden={currentSlide !== index}
              className="w-full flex-shrink-0" style={{ textWrap: 'pretty' }}
            >
              {slide}
            </div>
          ))}
        </div>

        {/* Navigation Arrows */}
        {showArrows && slides.length > 1 && isEnhanced && (
          <>
            <button
              onClick={prevSlide}
              className="absolute left-2 lg:left-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-md p-1.5 lg:p-2 shadow-md transition-all"
              aria-label="Go to previous slide"
            >
              <ChevronLeft className="w-4 lg:w-6 h-4 lg:h-6 text-gray-800" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-2 lg:right-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-md p-1.5 lg:p-2 shadow-md transition-all"
              aria-label="Go to next slide"
            >
              <ChevronRight className="w-4 lg:w-6 h-4 lg:h-6 text-gray-800" />
            </button>
          </>
        )}

        {/* Dots Indicator */}
        {showDots && slides.length > 1 && (
          <nav
            className="absolute bottom-2 lg:bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 lg:gap-2"
            aria-label="Carousel pagination"
          >
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => isEnhanced && goToSlide(index)}
                className={`
                  w-1.5 lg:w-2 h-1.5 lg:h-2 rounded-md transition-all
                  ${currentSlide === index 
                    ? 'bg-primary w-4 lg:w-6' 
                    : 'bg-gray-400 hover:bg-gray-600'
                  }
                `}
                aria-label={`Go to slide ${index + 1}`}
                aria-current={currentSlide === index ? 'true' : 'false'}
              />
            ))}
          </nav>
        )}
      </div>
    </>
  );
};